import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { currencyLabelOf, haChiaveGuid, prizeBandSeats, tournamentPrizes } from '@/lib/limulo/model';
import type { LimuloPrizeBand, LimuloTournament } from '@/types/limulo';
import type { ReactNode } from 'react';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatNumero } from '@/lib/fmt';
import { useGetLimuloWalletCurrenciesQuery, type LimuloWalletCurrency } from '@/services/limuloApi';

/**
 * Il tab **Montepremi** (reference B3, approvata da Luca I-94).
 *
 * ⚠️ **Nella TMA il montepremi non c'era affatto**: e' la sola riga su cui Limulo era
 * indietro rispetto a cio' che i giocatori vedono gia' in produzione (Laravel e app nativa
 * ce l'hanno).
 *
 * 🔴 **I DUE VUOTI RESTANO DUE.** `premi: []` ⇒ «questo torneo non ne prevede», che il
 * server ha detto. `premi: null` ⇒ «non dichiarato», che il server **non** ha detto. Un
 * vuoto solo per entrambi direbbe a un giocatore «questo torneo non ha premi» **quando
 * nessuno l'ha affermato**.
 * ⚠️ E la copy del `null` non puo' accusare l'organizzatore: `sanitizeTournamentPrizes` e'
 * **tutto-o-niente** — una sola fascia scritta male fa tornare `null` per l'intero
 * montepremi — quindi da qui «non dichiarato» e «scritto male» sono **la stessa risposta**.
 *
 * ⛔ **LA RIGA CHE NON SI TOGLIE**: «la consegna del premio non passa da questa schermata».
 * Il passaggio verso il portafoglio non attraversa il bordo mobile ed e' uno dei pezzi
 * ancora da concordare. *Un montepremi mostrato senza quella riga viene letto come una
 * promessa di pagamento.*
 *
 * ⚠️ **NON MISURATO**: quante fasce abbia un torneo vero e quali valute usi. Il disegno
 * regge da una a cinque fasce, ma nessun torneo reale e' stato letto.
 */
export function LimuloMontepremi({
  tournament,
  posizioneFinale,
}: {
  tournament: LimuloTournament;
  /**
   * La posizione del giocatore **a classifica DEFINITIVA**, per accendere la sua fascia.
   *
   * 🔴 **OGGI VALE SEMPRE `null`, E NON E' UN RIPIEGO PIGRO: NESSUNO PUO' CALCOLARLA.** Le
   * righe di `/v1/tournaments/:id/standings` arrivano **senza sanitizer** e non portano
   * nessuna chiave che si possa unire all'identita' dell'utente — `isMe` esiste sugli
   * incontri, non qui, e `profileId` il provider lo emette vuoto apposta. E' la stessa
   * famiglia del limite gia' dichiarato nel CLAUDE.md della sezione («l'app non puo' sapere
   * quale lato e' l'utente»), su un'altra rotta.
   * ⇒ Il parametro esiste perche' **il posto sia gia' suo** il giorno che una chiave di
   * giunzione arriva. ⛔ Non inventarla confrontando i nomi: due omonimi bastano a promettere
   * un premio alla persona sbagliata.
   *
   * ⛔ E **mai** su una classifica provvisoria: sarebbe una promessa.
   */
  posizioneFinale: number | null;
}) {
  return (
    <CatalogoMonete serve={haChiaveGuid(tournament)}>
      {(catalogo) => <Corpo tournament={tournament} posizioneFinale={posizioneFinale} catalogo={catalogo} />}
    </CatalogoMonete>
  );
}

/**
 * Il catalogo delle monete, per chi disegna una ricompensa (il tab Montepremi e la statistica
 * «Premio» del dettaglio): lo LEGGE solo con `serve` (almeno una chiave con forma di GUID, `haChiaveGuid`)
 * e lo passa a `children`; senza, `children(undefined)` e la query non si monta nemmeno — niente
 * chiamata in piu' sui tornei vecchi, e nessun hook del catalogo toccato (un banco che finge
 * `limuloApi` senza il catalogo non si rompe). ⚠️ `undefined` vale «non c'e'»: non chiesto, in lettura o
 * in errore sono la stessa cosa per chi etichetta, e `currencyLabelOf` ne fa l'etichetta generica.
 */
export function CatalogoMonete({
  serve,
  children,
}: {
  serve: boolean;
  children: (catalogo: readonly LimuloWalletCurrency[] | undefined) => ReactNode;
}) {
  return serve ? <LetturaCatalogo>{children}</LetturaCatalogo> : <>{children(undefined)}</>;
}

function LetturaCatalogo({
  children,
}: {
  children: (catalogo: readonly LimuloWalletCurrency[] | undefined) => ReactNode;
}) {
  // Cache lunga (un giorno, sull'endpoint): non si rilegge a ogni ritorno in primo piano.
  const { data } = useGetLimuloWalletCurrenciesQuery(undefined, { refetchOnFocus: false });
  return <>{children(data)}</>;
}

function Corpo({
  tournament,
  posizioneFinale,
  catalogo,
}: {
  tournament: LimuloTournament;
  posizioneFinale: number | null;
  /** `undefined` = non c'e' (non chiesto, in lettura, o in errore): un GUID non si risolve. */
  catalogo: readonly LimuloWalletCurrency[] | undefined;
}) {
  const { t } = useTranslation('limulo');
  const premi = tournamentPrizes(tournament);

  // ⛔ Un GUID non arriva mai a schermo: la regola (e le tre uscite) sta in `currencyLabelOf`.
  const etichetta = (chiave: string) =>
    currencyLabelOf(chiave, catalogo, () => t('prizes.currencyUnknown'));

  if (premi === null) {
    return <Vuoto title={t('prizes.undeclared.title')} body={t('prizes.undeclared.body')} />;
  }
  if (premi.length === 0) {
    return <Vuoto title={t('prizes.none.title')} body={t('prizes.none.body')} />;
  }

  // ⚠️ Una copia, poi l'ordinamento: `sort` muta l'array, e quello arriva dalla cache di RTK
  // Query — riordinarlo sul posto riordinerebbe il dato di chiunque altro lo legga.
  const fasce = [...premi].sort((a, b) => a.min - b.min);

  return (
    <div className="flex flex-col gap-3 pt-4">
      <ul className="flex flex-col gap-2">
        {fasce.map((fascia, i) => (
          <Fascia
            key={`${fascia.min}-${fascia.max}`}
            fascia={fascia}
            /* ⚠️ Solo la PRIMA si stacca, e «prima» e' quella ordinata: se il provider
               mandasse le fasce alla rovescia, l'accento finirebbe sull'ultimo posto. */
            prima={i === 0}
            tua={
              posizioneFinale !== null &&
              posizioneFinale >= fascia.min &&
              posizioneFinale <= fascia.max
            }
            posizione={posizioneFinale}
            etichetta={etichetta}
          />
        ))}
      </ul>

      {/* ⛔ NON SI TOGLIE. V. il blocco in testa al file. */}
      <p className="text-xs italic leading-relaxed text-fg-muted">{t('prizes.noDelivery')}</p>
    </div>
  );
}

function Fascia({
  fascia,
  prima,
  tua,
  posizione,
  etichetta,
}: {
  fascia: LimuloPrizeBand;
  prima: boolean;
  tua: boolean;
  posizione: number | null;
  etichetta: (chiave: string) => { testo: string; codice: boolean };
}) {
  const { t } = useTranslation('limulo');
  const lingua = useLinguaFormato();
  const posti = prizeBandSeats(fascia);
  const intervallo =
    fascia.min === fascia.max
      ? t('prizes.rank', { n: fascia.min })
      : t('prizes.rankRange', { from: fascia.min, to: fascia.max });

  return (
    <li
      className={cn(
        'card-edge flex items-start justify-between gap-4 rounded-xl bg-bg-secondary px-4 py-3',
        prima && 'border-l-2 border-l-brand',
        tua && 'bg-brand/10',
      )}
    >
      <div className="min-w-0">
        <p className={cn('text-sm font-extrabold tabular-nums', prima ? 'text-brand' : 'text-fg')}>
          {intervallo}
        </p>
        {/* ⚠️ Il conteggio dei posti solo quando e' PIU' DI UNO: «1 posto» accanto a «1º»
            ripete la stessa cosa con parole diverse, che e' rumore. */}
        {posti > 1 ? (
          <p className="text-[11px] tabular-nums text-fg-muted">{t('prizes.seats', { n: posti })}</p>
        ) : null}
        {tua && posizione !== null ? (
          <p className="mt-1 text-[11px] font-semibold text-brand">
            {t('prizes.yours', { n: posizione })}
          </p>
        ) : null}
      </div>

      {/* Una riga per valuta: `ricompense` e' `{valuta: importo}`.
          ⚠️ IL CODE SI MOSTRA GREZZO — nessuna mappa dichiarata verso un simbolo: inventarne uno
          affermerebbe una corrispondenza che nessuno ha dichiarato (stessa regola del `gameId`
          passthrough). La chiave puo' essere un `currencyId` (tornei nuovi, ADR-0187): `etichetta`
          la risolve nel `code` del catalogo, e un GUID che non si risolve non si stampa mai. */}
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        {Object.entries(fascia.ricompense).map(([valuta, importo]) => {
          const { testo, codice } = etichetta(valuta);
          return (
            <p key={valuta} className="text-sm tabular-nums text-fg">
              <span className="font-extrabold">{formatNumero(importo, lingua)}</span>{' '}
              <span className={cn('text-[11px] text-fg-muted', codice && 'font-mono')}>{testo}</span>
            </p>
          );
        })}
      </div>
    </li>
  );
}

function Vuoto({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-4 rounded-xl border border-border bg-bg-secondary p-4">
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      <p className="mt-1 text-sm leading-relaxed text-fg-muted">{body}</p>
    </div>
  );
}
